import type { Placement } from '@popperjs/core';
import { Popover, abbreviate, toString, usePopoverState } from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import { createRef, memo, useEffect, useState, type CSSProperties } from 'react';
import { Input, Wrapper } from './styles';

export const Bucket = memo(
  ({
    index = 0,
    shouldExpand = true,
    shouldHighlightOnClick = true,
    onUpdateSize = () => {},
    className,
    min,
    rawValue,
    increment,
    max,
    isReadOnly,
    isRightAligned,
    shouldShowFullValue,
    isLarge,
    style,
    width,
    precision,
    popOverPrecision,
    popOverPlacement = 'auto',
  }: {
    className?: string;
    index?: number;
    min?: number | string | undefined;
    rawValue: number | string | undefined;
    increment?: number | string | undefined;
    max?: number | string | undefined;
    onUpdateSize?: (index: number, newSize: string) => void;
    isReadOnly?: boolean;
    isRightAligned?: boolean;
    shouldShowFullValue?: boolean;
    isLarge?: boolean;
    shouldHighlightOnClick?: boolean;
    shouldExpand?: boolean;
    style?: CSSProperties;
    width?: CSSProperties['width'];
    precision?: number;
    popOverPrecision?: number;
    popOverPlacement?: Placement;
  }) => {
    const [numericalValue, setNumericalValue] = useState<number>();
    const [displayValue, setDisplayValue] = useState<string>();
    const [popOverDisplayValue, setPopOverDisplayValue] = useState<string>();
    const [shouldHighlight, setShouldHighlight] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const inputRef = createRef<HTMLInputElement>();
    const popOver = usePopoverState({
      trigger: 'hover',
      usePortal: true,
      placement: popOverPlacement,
    });

    function updateSize() {
      const stringifiedValue = toString(numericalValue);
      if (rawValue !== stringifiedValue) {
        onUpdateSize(index, stringifiedValue);
      }
    }

    function handleFocus(e: React.SyntheticEvent<any>) {
      popOver.close();
      e.stopPropagation();
      if (isReadOnly) {
        return false;
      }
      setIsExpanded(true);
    }

    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
      let newNumericalValue = parseFloat(e.target.value);
      if (max != null && newNumericalValue > parseFloat(max)) {
        newNumericalValue = parseFloat(max);
      } else if (newNumericalValue < parseFloat(min)) {
        newNumericalValue = parseFloat(min);
      }
      if (!Number.isNaN(newNumericalValue)) {
        setNumericalValue(newNumericalValue);
      }
      setDisplayValue(e.target.value);
    }

    function handleKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
      if (e.key.toLowerCase() === 'enter') {
        updateSize();
        setIsExpanded(false);
        e.preventDefault();
        return false;
      }
    }

    function handleBlur() {
      updateSize();
      setIsExpanded(false);
    }

    // Initial display value
    useEffect(() => {
      const parsedRawValue = parseFloat(rawValue);
      setNumericalValue(parsedRawValue);
      if (!isExpanded && !shouldShowFullValue) {
        setDisplayValue(abbreviate(parsedRawValue, { precision: precision }));
        if (popOverPrecision) {
          setPopOverDisplayValue(abbreviate(parsedRawValue, { precision: popOverPrecision }));
        }
      } else {
        setDisplayValue(toString(parsedRawValue));
      }
      // TODO Bucket highlighting and expanding doesn't work if we add all deps
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawValue]);

    // Display value
    useEffect(() => {
      if (typeof numericalValue !== 'undefined') {
        if (!isExpanded && !shouldShowFullValue) {
          setDisplayValue(abbreviate(numericalValue, { precision: precision }));
          if (popOverPrecision) {
            setPopOverDisplayValue(abbreviate(numericalValue, { precision: popOverPrecision }));
          }
        } else {
          setDisplayValue(toString(numericalValue));
          setShouldHighlight(true);
        }
      }
      // TODO Bucket highlighting and expanding doesn't work if we add all deps
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isExpanded, shouldShowFullValue]);

    // This must be called _after_ setDisplayValue() has been called,
    // otherwise the text will be deselected. Hence the "shouldHighlight" state.
    useEffect(() => {
      if (inputRef.current && shouldHighlight && shouldHighlightOnClick) {
        inputRef.current.select();
        setShouldHighlight(false);
      }
    }, [shouldHighlight, inputRef, shouldHighlightOnClick]);

    if (typeof displayValue === 'undefined') {
      return null;
    }

    return (
      <Wrapper className={className}>
        <Popover {...popOver}>
          <Input
            autoComplete="off"
            isExpanded={shouldExpand && isExpanded}
            isRightAligned={isRightAligned}
            max={max}
            min={min}
            isReadOnly={isReadOnly}
            readOnly={!isExpanded || isReadOnly}
            ref={inputRef}
            step={increment}
            value={displayValue}
            isLarge={isLarge}
            onBlur={handleBlur}
            onChange={handleInputChange}
            onClick={handleFocus}
            onFocus={handleFocus}
            onKeyPress={handleKeyPress}
            style={style}
            width={width}
          />
          {popOverPrecision && !isExpanded ? popOverDisplayValue : null}
        </Popover>
      </Wrapper>
    );
  },
  (p, n) => isEqual(p, n)
);
